import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, ShieldCheck } from "lucide-react";
import { api, getToken, setToken, errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { UploadGuide } from "@/components/admin/UploadGuide";
import { AnimeForm } from "@/components/admin/AnimeForm";
import { EpisodeUploader } from "@/components/admin/EpisodeUploader";
import { AnimeManager } from "@/components/admin/AnimeManager";
import { BulkUploader } from "@/components/admin/BulkUploader";
import { PasswordChange } from "@/components/admin/PasswordChange";

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const refresh = () => api.get("/anime").then((r) => setList(r.data)).catch(() => toast.error("Could not load anime list — please refresh"));

  useEffect(() => {
    refresh();
    if (!getToken()) return;
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => setToken(null));
  }, []);

  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password });
      setToken(r.data.token);
      setAuthed(true);
      toast.success("Welcome back, Admin");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    setToken(null);
    setAuthed(false);
    toast("Logged out");
  };

  if (!authed) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen pt-32 pb-20 flex items-center justify-center px-5">
        <motion.form initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }} onSubmit={login} className="kp-glass p-10 w-full max-w-md kp-card-clip">
          <Logo size={48} />
          <span className="kp-eyebrow block mt-8">Restricted</span>
          <h1 className="font-display font-bold text-3xl mt-2 tracking-tight">Admin Login</h1>
          <p className="text-[#94A3B8] text-sm mt-3">Only the admin can upload anime. Enter your password.</p>
          <div className="relative mt-8">
            <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" className="kp-input !pl-11" required />
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={busy} className="kp-btn kp-btn-primary w-full justify-center mt-5 disabled:opacity-60">
            <ShieldCheck size={14} /> {busy ? "Checking..." : "Enter Studio"}
          </button>
        </motion.form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen pt-28 sm:pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <span className="kp-eyebrow">Studio</span>
            <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 tracking-tight">Upload <span className="text-[#FF2A55]">Panel</span></h1>
          </div>
          <button data-testid="admin-logout-button" onClick={logout} className="kp-btn kp-btn-ghost !px-4 w-fit">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <UploadGuide />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-14">
          <div className="lg:col-span-5 flex flex-col gap-8">
            <AnimeForm onCreated={(a) => { refresh(); setSelected(a.id); }} />
            <BulkUploader list={list} selected={selected} setSelected={setSelected} onUploaded={() => { refresh(); bump(); }} />
            <PasswordChange />
          </div>
          <div className="lg:col-span-7 flex flex-col gap-8">
            <EpisodeUploader list={list} selected={selected} setSelected={setSelected} onUploaded={refresh} refreshKey={refreshKey} bump={bump} />
            <AnimeManager list={list} onChanged={() => { refresh(); bump(); }} />
          </div>
        </div>
      </div>
    </main>
  );
}
